import { useEffect } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import {
  GOOGLE_LOGO_URL,
  GOOGLE_MAPS_API_KEY,
  fetchGoogleCopyright,
  type GoogleSession,
} from './googleTiles';

const DEBOUNCE_MS = 400;

const escapeHtml = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

/**
 * The Google logo and the imagery credits for the visible area, both
 * required by the Map Tiles API terms while its tiles are on screen.
 */
export function GoogleAttribution({ session }: { session: GoogleSession | null }) {
  const map = useMap();

  useEffect(() => {
    const logo = new L.Control({ position: 'bottomleft' });
    logo.onAdd = () => {
      const img = L.DomUtil.create(
        'img',
        'leaflet-control pointer-events-none !mb-1 !ml-1',
      ) as HTMLImageElement;
      img.src = GOOGLE_LOGO_URL;
      img.alt = 'Google';
      img.width = 66;
      img.height = 26;
      return img;
    };
    logo.addTo(map);
    return () => {
      logo.remove();
    };
  }, [map]);

  useEffect(() => {
    const control = map.attributionControl;
    const key = GOOGLE_MAPS_API_KEY;
    if (!session || !control || !key) return;
    let shown: string | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let ctrl: AbortController | undefined;

    const show = (text: string | null) => {
      if (shown) control.removeAttribution(shown);
      shown = text ? escapeHtml(text) : null;
      if (shown) control.addAttribution(shown);
    };

    const refresh = () => {
      clearTimeout(timer);
      ctrl?.abort();
      timer = setTimeout(() => {
        ctrl = new AbortController();
        const signal = ctrl.signal;
        const b = map.getBounds();
        fetchGoogleCopyright(
          session,
          key,
          { north: b.getNorth(), south: b.getSouth(), east: b.getEast(), west: b.getWest() },
          map.getZoom(),
          signal,
        )
          .then((text) => {
            if (!signal.aborted) show(text);
          })
          .catch(() => {
            // keep the last credits on a failed lookup
          });
      }, DEBOUNCE_MS);
    };

    refresh();
    map.on('moveend', refresh);
    return () => {
      map.off('moveend', refresh);
      clearTimeout(timer);
      ctrl?.abort();
      show(null);
    };
  }, [map, session]);

  return null;
}
